import React, { useContext, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useParams } from "react-router-dom";
import SharedDataContext from "../../../SharedDataContext";
import {
  getProductAccessSettings,
  resetTeamProductAccessSettings,
  updateTeamProductAccess,
} from "../../../apis/workspace/teams";
import TableLoader from "../../Loaders/TableLoader";
import PrintDate from "../../Tags/PrintDate";
import { Switch } from "../../ui/switch";
import GenericModal from "../../GenericModal";
import useModal from "../../GenericModal/useModal";
import UpdateProductSettingsModal from "./UpdateProductSettingsModal";
import toast from "react-hot-toast";
import { Pencil, RefreshCw } from "lucide-react";
import { ResetIcon } from "@radix-ui/react-icons";

export default function ProductAccess() {
  const { teamId } = useParams();
  const queryClient = useQueryClient();
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const {
    data: products = [],
    isLoading,
    refetch,
  } = useQuery(
    ["team_products", activeWorkspace?.id],
    () =>
      getProductAccessSettings({
        teamId: teamId,
        query: "per_page=100",
        options: {
          headers: {
            "X-Workspace-Id": activeWorkspace?.id,
          },
        },
      }),
    {
      enabled: Boolean(activeWorkspace?.id) && !!teamId,
      select: (response) => response?.data ?? [],
      staleTime: 300_000,
    }
  );

  console.log("products", products);
  const { isOpen, openModal, closeModal } = useModal();
  const [currentProduct, setCurrentProduct] = useState({});

  const updateTeamProductAccessFn = useMutation("update_team_product_access", updateTeamProductAccess, {
    onSuccess(data, variables, context) {
      toast.success(data?.message);
      queryClient.invalidateQueries("team_products");
    },
  });
  const toggleAccess = (productId, toggle) => {
    if (updateTeamProductAccessFn.isLoading) {
      return;
    }
    updateTeamProductAccessFn.mutate({
      teamId: teamId,
      productId: productId,
      data: { has_access: toggle },
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  const resetTeamProductAccessSettingsFn = useMutation(
    "reset_team_product_access_settings",
    resetTeamProductAccessSettings,
    {
      onSuccess(data, variables, context) {
        toast.success(data?.message);
        queryClient.invalidateQueries("team_products");
      },
    }
  );
  const handleResetTeamProductAccessSettings = (productId) => {
    if (resetTeamProductAccessSettingsFn.isLoading) {
      return;
    }
    resetTeamProductAccessSettingsFn.mutate({
      teamId: teamId,
      productId: productId,
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  if (isLoading) {
    return <TableLoader />;
  }
  return (
    <div>
      {products && products.length > 0 && (
        <div className="mt-6 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden border border-border rounded-[--radius]">
                <table className="min-w-full divide-y divide-border table-fixed">
                  <thead className="bg-gray-100">
                    <tr>
                      <th scope="col" className="w-2/12 px-3 py-3.5 text-left text-sm font-semibold">
                        Name
                      </th>
                      <th scope="col" className="w-3/12 px-3 py-3.5 text-left text-sm font-semibold">
                        Credit Limit
                      </th>
                      <th scope="col" className="w-2/12 px-3 py-3.5 text-left text-sm font-semibold">
                        Credit Limit Type
                      </th>
                      <th scope="col" className="w-3/12 px-3 py-3.5 text-left text-sm font-semibold">
                        Last Used
                      </th>
                      <th scope="col" className="w-2/12 px-3 py-3.5 text-left text-sm font-semibold">
                        Toggle access
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-background">
                    {products.map((item) => (
                      <tr>
                        <td className="w-2/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                          {item?.metric_display_name}
                        </td>
                        <td className="w-3/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                          {item?.set_credit_limit ? (
                            <span className="text-brand-700 font-bold inline-flex items-end leading-none space-x-2 ">
                              {item?.credit_limit_value}{" "}
                              <span className="capitalize ml-1">{item?.credit_reset_timeline}</span>
                              {/* <Pencil width={16} height={16} /> */}
                            </span>
                          ) : (
                            <span
                              onClick={() => {
                                openModal();
                                setCurrentProduct(item);
                              }}
                              className="text-purple-700 font-bold inline-block cursor-pointer"
                            >
                              Set a limit
                            </span>
                          )}
                        </td>
                        <td className="w-2/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                          {item?.credit_limit_type || "-"}
                        </td>
                        <td className="w-3/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                          {item?.updated_at ? <PrintDate date={item?.updated_at} /> : "-"}
                        </td>
                        <td className="w-2/12 whitespace-nowrap px-3 py-4 text-sm text-paragraph">
                          <div className="flex w-full items-center space-x-4 justify-between">
                            <Switch
                              checked={item?.has_access}
                              onCheckedChange={(checked) => {
                                toggleAccess(item?.metric_name, checked);
                              }}
                            />
                            <div className="inline-flex items-center space-x-4">
                              {item.set_credit_limit && (
                                <>
                                  <Pencil
                                    data-tooltip-content="Edit"
                                    data-tooltip-id="react__tooltip__app"
                                    className="cursor-pointer"
                                    width={18}
                                    height={18}
                                    onClick={() => {
                                      openModal();
                                      setCurrentProduct(item);
                                    }}
                                  />
                                </>
                              )}
                              {item?.credit_limit_value && (
                                <div>
                                  {resetTeamProductAccessSettingsFn.isLoading &&
                                  currentProduct &&
                                  item.metric_name === currentProduct.metric_name ? (
                                    <div className="loader_circle"></div>
                                  ) : (
                                    <RefreshCw
                                      width={18}
                                      height={18}
                                      data-tooltip-content="Reset Limit"
                                      data-tooltip-id="react__tooltip__app"
                                      className="cursor-pointer"
                                      onClick={() => {
                                        setCurrentProduct(item);
                                        handleResetTeamProductAccessSettings(item?.metric_name);
                                      }}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      <GenericModal isOpen={isOpen} closeModal={closeModal}>
        <UpdateProductSettingsModal
          closeModal={closeModal}
          activeWorkspace={activeWorkspace}
          data={currentProduct}
          refetch={refetch}
        />
      </GenericModal>
    </div>
  );
}
